import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, Plus } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { NotificationBellPremium } from "@/components/notifications/NotificationBellPremium";
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { CreateStoryPremium } from "@/components/social/CreateStoryPremium";
import { StoryViewer } from "@/components/social/StoryViewer";
import { OfflineBadge } from "@/components/sync/OfflineBadge";
import { AppLogsPanel } from "@/components/debug/AppLogsPanel";
import { ThemeToggle } from "@/components/ui/theme-toggle";

export const HomeHeader = () => {
  const navigate = useNavigate();
  const [createStoryOpen, setCreateStoryOpen] = useState(false);
  const [viewOwnStories, setViewOwnStories] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const [tapTimer, setTapTimer] = useState<NodeJS.Timeout | null>(null);

  // Helper para exibir bullet e nível baseado em shirt_level
  const getShirtDisplay = (level: string) => {
    const levels = {
      red: { 
        bgColor: 'bg-red-500',
        label: 'Nível 1',
        sublabel: 'Camisa Vermelha',
        description: 'Iniciante 🔴 Camisa Vermelha'
      },
      blue: { 
        bgColor: 'bg-blue-500',
        label: 'Nível 2',
        sublabel: 'Camisa Azul',
        description: 'Intermediário 🔵 Camisa Azul'
      },
      gold: { 
        bgColor: 'bg-primary',
        label: 'Nível 3',
        sublabel: 'Camisa Laranja',
        description: 'Avançado 🟠 Camisa Laranja'
      }
    };
    return levels[level as keyof typeof levels] || levels.red;
  };

  // Fetch user profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id, name, avatar_url, shirt_level')
        .eq('id', user.id)
        .single();

      return profile;
    }
  });

  // Fetch real followers count
  const { data: followersCount = 0 } = useQuery({
    queryKey: ['followers-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      
      const { count } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', profile.id);
      
      return count || 0;
    },
    enabled: !!profile?.id
  });

  // Fetch user's own stories
  const { data: userStories } = useQuery({
    queryKey: ['user-stories', profile?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data } = await supabase
        .from('stories')
        .select('*')
        .eq('user_id', user.id)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      return data || [];
    },
    enabled: !!profile
  });

  // Realtime subscription for notifications
  useEffect(() => {
    let channel: any;
    let isSubscribed = false;
    
    const setupRealtime = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      channel = supabase
        .channel('notifications-updates')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          () => window.location.reload()
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            isSubscribed = true;
          }
        });
    };

    setupRealtime();

    return () => {
      if (channel && isSubscribed) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  if (profileLoading) {
    return (
    <header 
      className="bg-background border-b border-border/20 shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
        style={{
          height: '52px',
          paddingLeft: `calc(env(safe-area-inset-left, 0px) + 12px)`,
          paddingRight: `calc(env(safe-area-inset-right, 0px) + 12px)`,
        }}
        data-fixed-header="true"
      >
        {/* Área fixa de 52px para conteúdo - padrão iOS + respiro */}
        <div className="h-[52px] flex items-center justify-between py-1">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-9 h-9 rounded-full" />
            <Skeleton className="w-9 h-9 rounded-full" />
            <Skeleton className="w-9 h-9 rounded-full" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      <header 
        className="bg-background border-b border-border/20 shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
        style={{
          height: '52px',
          paddingLeft: `calc(env(safe-area-inset-left, 0px) + 12px)`,
          paddingRight: `calc(env(safe-area-inset-right, 0px) + 12px)`,
          backgroundColor: 'hsl(var(--background))',
        }}
        data-fixed-header="true"
      >
        {/* Área fixa de 52px para conteúdo - padrão iOS navigation bar + respiro */}
        <div className="h-[52px] flex items-center justify-between py-1">
          {/* User Info with Story Ring */}
          <div className="flex items-center gap-2">
            {/* Story Ring (como Instagram) + 5-tap gesture para logs */}
            <button
              onClick={() => {
                // Detectar 5 toques rápidos para abrir logs
                const newCount = tapCount + 1;
                
                if (tapTimer) {
                  clearTimeout(tapTimer);
                }
                
                if (newCount === 5) {
                  // 5 toques! Abrir painel de logs
                  setLogsOpen(true);
                  setTapCount(0);
                  setTapTimer(null);
                } else {
                  setTapCount(newCount);
                  
                  // Reset após 2 segundos
                  const timer = setTimeout(() => {
                    setTapCount(0);
                    setTapTimer(null);
                    
                    // Comportamento normal após timeout
                    if (userStories && userStories.length > 0) {
                      setViewOwnStories(true);
                    } else {
                      setCreateStoryOpen(true);
                    }
                  }, 2000);
                  
                  setTapTimer(timer);
                }
              }}
              className="relative group transition-all active:scale-95"
            >
              {/* Story Ring - Tamanho reduzido para 40px (w-10 h-10) */}
              <div className={`p-[2px] rounded-full transition-all ${
                userStories && userStories.length > 0
                  ? 'bg-gradient-to-tr from-[#FF5404] via-[#FF006E] to-[#FFC300]'
                  : 'bg-gradient-to-tr from-[#FF5404] to-[#FF9B19] animate-pulse'
              }`}>
                <Avatar className="w-9 h-9 border-2 border-background">
                  <AvatarImage src={profile?.avatar_url || undefined} />
                  <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/5 text-primary font-semibold text-sm">
                    {profile?.name?.[0]?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              {/* Botão + menor */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        setCreateStoryOpen(true);
                      }}
                      className="absolute bottom-0 right-0 w-4 h-4 bg-gradient-to-br from-[#FF5404] to-[#FF9B19] rounded-full flex items-center justify-center border-2 border-background shadow-lg animate-bounce cursor-pointer hover:scale-110 transition-transform"
                    >
                      <Plus className="w-2 h-2 text-white" strokeWidth={3} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>Adicionar Story</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </button>

            <button 
              onClick={() => navigate('/profile')}
              className="text-left group transition-all"
            >
              <h2 className="text-sm font-bold text-foreground group-hover:text-primary transition-colors leading-tight">
                {profile?.name || (profileLoading ? 'Carregando...' : 'Sem nome')}
              </h2>
              <div className="flex items-center gap-1.5">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1 cursor-help">
                        <div 
                          className={`w-2 h-2 rounded-full ${getShirtDisplay(profile?.shirt_level || 'red').bgColor}`}
                        />
                        <span className="text-[10px] font-medium text-muted-foreground">
                          {getShirtDisplay(profile?.shirt_level || 'red').label}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{getShirtDisplay(profile?.shirt_level || 'red').description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                  <Users className="w-2.5 h-2.5" />
                  <span className="font-medium">{followersCount || 0}</span>
                </div>
              </div>
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            <OfflineBadge />
            <ThemeToggle />
            <NotificationBellPremium />
          </div>
        </div>
      </header>

      {/* Create Story Drawer - Premium iOS Style */}
      <Drawer open={createStoryOpen} onOpenChange={setCreateStoryOpen}>
        <DrawerContent className="h-[92vh] rounded-t-[24px]">
          <CreateStoryPremium onClose={() => setCreateStoryOpen(false)} />
        </DrawerContent>
      </Drawer>

      {/* View Own Stories Dialog */}
      {userStories && userStories.length > 0 && (
        <Dialog open={viewOwnStories} onOpenChange={setViewOwnStories}>
          <DialogContent className="max-w-full h-full p-0 bg-black border-none" aria-describedby="own-story-viewer">
            <span id="own-story-viewer" className="sr-only">
              Visualizando seus stories
            </span>
            <StoryViewer
              stories={userStories.map(s => ({ 
                ...s, 
                media_type: s.media_type as 'image' | 'video',
                user: { name: profile?.name || 'Você', avatar_url: profile?.avatar_url }, 
                viewed: false 
              }))}
              onClose={() => setViewOwnStories(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* App Logs Panel - Ativado por 5 toques no avatar */}
      <AppLogsPanel open={logsOpen} onOpenChange={setLogsOpen} />
    </>
  );
};
